/**
 * Brand OS history: who changed what and when, Beatly or the client's team (GET /v1/brands/me/history).
 * A side sheet opened from the page header; loads each time it opens so it shows the latest saves.
 */
import { useEffect, useState } from 'react';
import { History, Loader2, X } from 'lucide-react';
import { brandHistory, editorLabel, fieldLabel, type BrandHistoryEntry } from './brandOsApi';

function when(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function HistoryPanel({ brandId, open, onClose }: { brandId?: string; open: boolean; onClose: () => void }) {
  const [entries, setEntries] = useState<BrandHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let on = true;
    setEntries(null); setError(null);
    brandHistory(brandId).then((e) => { if (on) setEntries(e); }).catch(() => { if (on) setError('Could not load the history. Try again in a moment.'); });
    return () => { on = false; };
  }, [open, brandId]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#101828]/30" onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-label="Brand OS history" className="flex h-full w-full max-w-[380px] flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-[#eaecf0] px-5 py-4">
          <History className="h-4 w-4 text-[#667085]" />
          <span className="flex-1 text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>History</span>
          <button type="button" aria-label="Close history" onClick={onClose} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && <p className="text-[13px] text-[#b42318]">{error}</p>}
          {!error && entries === null && <div className="flex items-center gap-2 text-[13px] text-[#98a2b3]"><Loader2 className="h-4 w-4 animate-spin" /> Loading</div>}
          {entries && entries.length === 0 && <p className="text-[13px] text-[#667085]">No changes yet. Every save shows here with who made it.</p>}
          {entries && entries.length > 0 && (
            <ol className="space-y-3">
              {entries.map((e, i) => (
                <li key={`${e.at}-${i}`} className="rounded-xl border border-[#eaecf0] px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{e.by?.name || 'Someone'}</span>
                    <span data-kind={e.by?.kind} className={e.by?.kind === 'beatly' ? 'rounded-full bg-[#f4ebff] px-2 py-0.5 text-[11px] text-[#6941c6]' : 'rounded-full bg-[#f2f4f7] px-2 py-0.5 text-[11px] text-[#475467]'} style={{ fontWeight: 600 }}>
                      {editorLabel(e.by?.kind === 'beatly' ? 'beatly' : 'client')}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-[#667085]">{when(e.at)}</div>
                  {e.fields?.length > 0 && <div className="mt-1 text-[12.5px] text-[#344054]">Changed {[...new Set(e.fields.map(fieldLabel))].join(', ')}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}
